// src/pages/Enseignants/Palmares/EditPalmaresModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaSave, FaTimes, FaEraser } from "react-icons/fa";
import { updatePalmares } from "../../../services/palmaresServices";
import { getAllEvents } from "../../../services/eventServices"; // <<-- IMPORTANT
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";

/* Switch accessibilité pour la visibilité */
function VisibilitySwitch({ checked, onChange, label }) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onChange}
        role="switch"
        aria-checked={checked}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
          checked ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-700"
        }`}
        title={checked ? "Affiché sur l’accueil" : "Masqué de l’accueil"}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            checked ? "translate-x-5" : "translate-x-1"
          }`}
        />
      </button>
      {label ? (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {label}
        </span>
      ) : null}
    </div>
  );
}

const emptyForm = {
  event_palmares_id: "",         // <<-- remis
  date_palmares: "",
  titre_palmares: "",
  resultats_palmares: "",
  classement_palmares: "",
  observations_palmares: "",
  is_active: true,
};

export default function EditPalmaresModal({ isOpen, onClose, refreshList, palmares }) {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  // Événements (pour le select filtrable)
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventQuery, setEventQuery] = useState("");

  // Charge les événements à l’ouverture
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        setEventsLoading(true);
        const data = await getAllEvents();
        setEvents(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Erreur getAllEvents:", e);
        toast.error("Impossible de charger la liste des événements.");
      } finally {
        setEventsLoading(false);
      }
    })();
  }, [isOpen]);

  // Helpers affichage évènement
  const eventTitle = (ev) =>
    ev?.titre_event || ev?.titre || ev?.name || ev?.label || `Événement #${ev?.id || "?"}`;
  const eventDateStr = (ev) => {
    const d = ev?.date_event || ev?.date || ev?.start_date || ev?.startDate;
    return d ? new Date(d).toLocaleDateString("fr-FR") : "";
  };
  const eventOptionLabel = (ev) => {
    const t = eventTitle(ev);
    const ds = eventDateStr(ev);
    return ds ? `${t} — ${ds}` : t;
  };

  // Filtrage client
  const filteredEvents = useMemo(() => {
    const q = eventQuery.trim().toLowerCase();
    if (!q) return events;
    return events.filter((ev) => {
      const t = eventTitle(ev).toLowerCase();
      const ds = eventDateStr(ev).toLowerCase();
      return t.includes(q) || ds.includes(q);
    });
  }, [events, eventQuery]);

  // Hydrate le formulaire à l’ouverture
  useEffect(() => {
    if (!isOpen) return;
    const f = { ...emptyForm };
    if (palmares) {
      f.event_palmares_id = palmares.event_palmares_id ?? "";
      f.date_palmares = palmares.date_palmares
        ? new Date(palmares.date_palmares).toISOString().slice(0, 10)
        : "";
      f.titre_palmares = palmares.titre_palmares || "";
      f.resultats_palmares = palmares.resultats_palmares || "";
      f.classement_palmares = palmares.classement_palmares || "";
      f.observations_palmares = palmares.observations_palmares || "";
      f.is_active = !!palmares.is_active;
    }
    setForm(f);
    setEventQuery(""); // reset de la recherche
  }, [isOpen, palmares]);

  // Fermer via ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const validate = () => {
    if (!form.date_palmares) {
      toast.warn("La date est requise.");
      return false;
    }
    if (!form.titre_palmares.trim()) {
      toast.warn("Le titre est requis.");
      return false;
    }
    // event_palmares_id peut être vide si tu autorises des palmarès non rattachés
    return true;
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!palmares?.id) return;
      if (!validate()) return;

      setSubmitting(true);
      try {
        await updatePalmares(palmares.id, {
          ...form,
          // Si ton API attend un number:
          // event_palmares_id: form.event_palmares_id ? Number(form.event_palmares_id) : null,
        });
        toast.success("Palmarès mis à jour avec succès !");
        refreshList?.();
        onClose?.();
      } catch (error) {
        console.error("Erreur lors de la mise à jour du palmarès", error);
        toast.error("Erreur lors de la mise à jour du palmarès.");
      } finally {
        setSubmitting(false);
      }
    },
    [form, palmares?.id, onClose, refreshList]
  );

  const selectedEvent = useMemo(
    () => events.find((ev) => String(ev.id) === String(form.event_palmares_id)),
    [events, form.event_palmares_id]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay cliquable */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Modifier un palmarès
          </h3>
          <p className="mt-1 text-white/90 text-xs text-center">
            Ajustez les informations ci-dessous puis enregistrez.
          </p>
        </div>

        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-4">
          {/* Événement (recherche + select) */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Événement lié (optionnel)
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {/* Recherche */}
              <input
                type="text"
                value={eventQuery}
                onChange={(e) => setEventQuery(e.target.value)}
                placeholder="Rechercher un événement…"
                className="sm:col-span-2 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />

              {/* Clear */}
              <button
                type="button"
                onClick={() => {
                  setEventQuery("");
                  setForm((f) => ({ ...f, event_palmares_id: "" }));
                }}
                className="inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 ring-1 ring-slate-300 dark:ring-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                title="Effacer la sélection"
              >
                <FaEraser className="opacity-80" />
                Effacer
              </button>
            </div>

            {/* Select (filtré) */}
            <div className="mt-2">
              <select
                name="event_palmares_id"
                value={form.event_palmares_id ?? ""}
                onChange={onChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                disabled={eventsLoading}
              >
                <option value="">— Aucun événement —</option>
                {filteredEvents.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {eventOptionLabel(ev)}
                  </option>
                ))}
              </select>
            </div>

            {/* Rappel sélection */}
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Sélection actuelle :{" "}
              <span className="font-medium">
                {selectedEvent ? eventOptionLabel(selectedEvent) : "—"}
              </span>
            </p>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Date
            </label>
            <input
              type="date"
              name="date_palmares"
              value={form.date_palmares}
              onChange={onChange}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            />
          </div>

          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Titre de l’événement / compétition
            </label>
            <input
              type="text"
              name="titre_palmares"
              value={form.titre_palmares}
              onChange={onChange}
              placeholder="Ex : 1er au Tournoi Régional"
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            />
          </div>

          {/* Résultats / Classement */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Résultats (optionnel)
              </label>
              <input
                type="text"
                name="resultats_palmares"
                value={form.resultats_palmares}
                onChange={onChange}
                placeholder="Ex : 3 combats gagnés, 1 perdu"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Classement (optionnel)
              </label>
              <input
                type="text"
                name="classement_palmares"
                value={form.classement_palmares}
                onChange={onChange}
                placeholder="Ex : 2e"
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          </div>

          {/* Observations */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Observations (optionnel)
            </label>
            <textarea
              name="observations_palmares"
              value={form.observations_palmares}
              onChange={onChange}
              placeholder="Notes, contexte, remarques…"
              rows={4}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>

          {/* Visibilité */}
          <div className="pt-1">
            <VisibilitySwitch
              checked={!!form.is_active}
              onChange={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
              label="Afficher ce palmarès sur la page d’accueil"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
              disabled={submitting}
            />
            <Button
              text={submitting ? "En cours…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              className={`!rounded-xl gap-2 ${submitting ? "opacity-60 pointer-events-none" : ""}`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
